import React from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import * as actionCreators from "app/static/frontend/sheets/sheet-action-creators"
import { updateUserdata } from "shared/userdata/userdata-action-creators"
import sheetConstants from "app/static/frontend/sheets/constants"
import ResponsiveSheet from "app/static/frontend/sheets/containers/ResponsiveSheet"
import { resourceTableConnection } from "app/static/frontend/sheets/selectors/sheet-selectors"
import LoadingSpinner from "app/static/frontend/components/LoadingSpinner"
import { segueToOutboundTextingCompose } from "outbound-texting/action-creators"

// helpers and constants
import debounce from "lodash.debounce"
import BACKENDERROR from "app/static/frontend/imports/backenderror"
import { swalContents } from "app/static/frontend/sheets/components/helpTexts"
import { areSheetColumnsEqual } from "app/static/frontend/sheets/helper-functions"

import { EmptyState, DropdownButton } from "quorumdesign"
import { Button } from "@quorumus/plum"
import { Icon } from "@/components"
import { EmptyScreen } from "@/components/EmptyScreen/EmptyScreen"
import { segueToUrl } from "global-action-creators"

export class ResourceTable extends React.Component {
    constructor(props) {
        super(props)

        this.debouncedLoadTable = debounce((nextProps) => this.load(nextProps), sheetConstants.REQUEST_DEBOUNCE_TIME)
    }

    componentDidMount() {
        this.load()
        window.dangerouslyTriggerResourceTableRefresh = this.load
        // we need these this classname to prevent the page other than the
        // resource table from scrolling
        $("body").addClass("no-scroll")

        if (this.props.emptyStateCallBack && this.props.loadingCallBack) {
            this.props.emptyStateCallBack(this.props.shouldShowEmptyState)
            this.props.loadingCallBack(this.props.shouldShowLoadingSpinner)
        }
    }

    componentDidUpdate(prevProps) {
        const conditions = [
            // the row filter changed
            JSON.stringify(prevProps.rowFilter) !== JSON.stringify(this.props.rowFilter),
            // if the sort key changed
            prevProps.sortKey !== this.props.sortKey,
            !areSheetColumnsEqual(prevProps.columns, this.props.columns),
        ]
        if (conditions.any()) {
            this.debouncedLoadTable(this.props)
        }

        if (this.props.emptyStateCallBack && this.props.loadingCallBack) {
            if (prevProps.shouldShowEmptyState !== this.props.shouldShowEmptyState) {
                this.props.emptyStateCallBack(this.props.shouldShowEmptyState)
            }
            if (prevProps.shouldShowLoadingSpinner !== this.props.shouldShowLoadingSpinner) {
                this.props.loadingCallBack(this.props.shouldShowLoadingSpinner)
            }
        }
    }

    componentWillUnmount() {
        // remove the classnames on unmount
        $("body").removeClass("no-scroll")
        if (this.networkRequest) {
            this.networkRequest.abort()
            delete this.networkRequest
        }
    }

    loadNextInterval = () => {
        const {
            loadResourceTableInterval,
            id,
            rowResource,
            rowData,
            rowFilter,
            sortKey,
            scrollInterval,
            cellStateMappingFunc,
        } = this.props
        loadResourceTableInterval(
            id,
            rowResource,
            rowData.size,
            scrollInterval,
            rowFilter,
            sortKey,
            cellStateMappingFunc,
        )
    }
    load = async (nextProps = {}) => {
        // if we have a pending request, abort it
        this.abortPendingRequest()
        try {
            // if we are passed new props, use them
            if (nextProps.id) {
                this.networkRequest = this.props.loadResourceTable(
                    nextProps.id,
                    nextProps.rowResource,
                    nextProps.rowFilter,
                    nextProps.sortKey,
                    nextProps.scrollInterval,
                    nextProps.cellStateMappingFunc,
                    nextProps.canOrderRows,
                )
                await this.networkRequest
            } else {
                this.networkRequest = this.props.loadResourceTable(
                    this.props.id,
                    this.props.rowResource,
                    this.props.rowFilter,
                    this.props.sortKey,
                    this.props.scrollInterval,
                    this.props.cellStateMappingFunc,
                    this.props.canOrderRows,
                )
                await this.networkRequest
            }
        } catch (error) {
            await BACKENDERROR(error, swalContents.failLoadResourceTable)
        }
    }

    // abort the pending request, if it exists
    abortPendingRequest = () => {
        if (this.networkRequest) {
            this.networkRequest.abort()
            delete this.networkRequest
        }
    }

    /**
     * Build the Empty State for the current DataType. If this is the Undelete/Recover Items table,
     * we want to render recovery specific text, instead of text prompting the user to create an entity.
     *
     * @todo Perhaps this should be moved into the Feature enum, but there are a lot of recoverable QDTs that don't have associated
     * Features, and I don't think they all need one to support this one usecase.
     */
    renderEmptyState() {
        const dataType = DjangIO.app.models.QuorumDataType.by_value(this.props.dataType)
        let { feature } = this.props

        if (this.props.isUndelete) {
            feature = {
                empty_state_title: `Recover ${dataType.plural}`,
                empty_state_paragraph: `If any of your deleted ${dataType.plural} are able to be recovered, they will be displayed here.`,
                icon: "archive",
                iconFamily: "fad",
            }
        }

        if (dataType === DjangIO.app.models.QuorumDataType.bulk_sms) {
            return (
                <EmptyScreen
                    buttonText="Add text"
                    buttonOnClick={this.props.segueToOutboundTextingCompose}
                    icon="comment"
                    iconFamily="far"
                    message="Send a SMS or MMS to notify contacts and supporters about your campaigns"
                    title="No texts found"
                />
            )
        }

        return <EmptyState {...feature}>{!this.props.isUndelete && this.renderEmptyStateActions()}</EmptyState>
    }

    /**
     * Build the actions that are rendered beneath the Empty State.
     * Render only the actions that have been passed a path to segue to.
     */
    renderEmptyStateActions() {
        const { createButtonUrl, createButtonOnClick, createButtonText, helpCenterUrl, createButtonMenuOptions } =
            this.props
        return (
            <>
                {(createButtonUrl || createButtonOnClick) && (
                    <Button
                        leftSection={<Icon icon="plus" iconFamily="fas" />}
                        onClick={() => {
                            if (createButtonOnClick) {
                                return this.props.createButtonOnClick()
                            }

                            return this.props.segueToUrl(createButtonUrl)
                        }}
                        dataCy="empty-state-create-button"
                    >
                        {createButtonText}
                    </Button>
                )}
                {createButtonMenuOptions && (
                    /*
                     * Expects an array of {text: string, onCLick: function} objects for the DropdownButton.
                     * See DownloadCenterListView
                     */
                    <DropdownButton
                        text={createButtonText}
                        leftIcon="plus"
                        menuOptions={createButtonMenuOptions}
                        dataCy="empty-state-create-button-dropdown"
                    />
                )}
                {helpCenterUrl && (
                    <Button
                        variant="tertiary"
                        onClick={() => {
                            window.open(helpCenterUrl, "_blank", "noopener,noreferrer")
                        }}
                        dataCy="empty-state-help-button"
                    >
                        Learn More
                    </Button>
                )}
            </>
        )
    }

    render() {
        // a Loading spinner is shown when the conditions in the selector are met
        if (this.props.shouldShowLoadingSpinner) {
            return <LoadingSpinner id="page-loading-no-top" />
        }

        /*
         * If there are noResults and there is an associated Feature, render the EmptyState
         * Feature contains the empty_state_title, empty_state_paragraph, and icon props that EmptyState consumes.
         * It is derived from props.dataType
         */
        return this.props.shouldShowEmptyState ? (
            this.renderEmptyState()
        ) : (
            <ResponsiveSheet
                load={this.load}
                loadNextInterval={this.loadNextInterval}
                {...this.props}
                sheetId={this.props.sheetId || this.props.id}
            />
        )
    }
}

ResourceTable.propTypes = {
    canAddColumns: PropTypes.bool,
    canDownload: PropTypes.bool,
    canEdit: PropTypes.bool,
    canFitInMemory: PropTypes.bool,
    canHideColumns: PropTypes.bool,
    canRemoveColumns: PropTypes.bool,
    canRemoveRows: PropTypes.bool,
    canRenameColumns: PropTypes.bool,
    canRenameSheet: PropTypes.bool,
    createButtonUrl: PropTypes.string,
    createButtonMenuOptions: PropTypes.array,
    createButtonOnClick: PropTypes.func,
    createButtonText: PropTypes.string,
    dataType: PropTypes.number,
    emptyStateCallBack: PropTypes.func,
    feature: PropTypes.object,
    helpCenterUrl: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    isReorderable: PropTypes.bool,
    isResizable: PropTypes.bool,
    isResourceTable: PropTypes.bool,
    // Whether this ResourceTable is the Undelete/Recover Items table, and has a unique empty state
    isUndelete: PropTypes.bool,
    loadingCallBack: PropTypes.func,
    loadResourceTable: PropTypes.func.isRequired,
    name: PropTypes.string,
    rowFilter: PropTypes.object,
    segueToUrl: PropTypes.func,
    sheetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    shouldShowEmptyState: PropTypes.bool,
    shouldShowLoadingSpinner: PropTypes.bool,
    showBackButton: PropTypes.bool,
    showOptions: PropTypes.bool,
    updateUserdata: PropTypes.func.isRequired,
}

ResourceTable.defaultProps = {
    canAddColumns: false,
    canDownload: false,
    canEdit: false,
    canFitInMemory: false,
    canHideColumns: false,
    canRemoveColumns: false,
    canRemoveRows: false,
    canRenameColumns: false,
    canRenameSheet: false,
    isReorderable: false,
    isResizable: false,
    isResourceTable: true,
    isUndelete: false,
    showBackButton: false,
    showOptions: false,
}

/*
we need to include mergeProps because we want to support overwriting some props that
are passed down from different places
* However, by assigning ownProps after stateProps, we actually override the default way props are
* overriden in redux. Since we get the rowFilter prop from our parent, we need to make sure we are using
* passing the selector result after ownProps
*/
const mergeProps = (stateProps, dispatchProps, ownProps) =>
    Object.assign({}, stateProps, dispatchProps, ownProps, {
        rowFilter: { ...stateProps.rowFilter, for_resource_table: true },
    })

export default connect(
    (state, props) => resourceTableConnection(state, props),
    { ...actionCreators, updateUserdata, segueToUrl, segueToOutboundTextingCompose },
    mergeProps,
)(ResourceTable)
